import { checkElementAccess } from './checkElementAccess';
import { stripHtml } from './sanitize';

export const FEATURED_MAX = 10;

const featuredTypes = ['challenge', 'quest', 'badge', 'level', 'collection'];
const featuredItemTypes = ['inventory', 'realworld', 'contribution', 'collectible', 'prizepack'];

const typeLabels = {
  'challenge': 'Challenge',
  'quest': 'Quest',
  'badge': 'Badge',
  'level': 'Level',
  'collection': 'Collection',
  'inventory': 'Game Item',
  'realworld': 'Real Item',
  'contribution': 'Contribution',
  'collectible': 'Collectible',
  'prizepack': 'Prize Pack',
}

// element types that can be featured on the player dashboard
export function isFeaturableElement(element) {
  if (!element || element.status !== 'active') return false;
  if (featuredTypes.includes(element.type)) return true;
  return element.type === 'item' && featuredItemTypes.includes(element.opt_itemType);
}

export function getFeaturedTypeKey(element) {
  return element.type === 'item' ? element.opt_itemType : element.type;
}

export function getFeaturedTypeLabel(element) {
  return typeLabels[getFeaturedTypeKey(element)] ?? element.type;
}

export const featuredTypeLabelList = Object.values(typeLabels);

// supports the legacy single featuredElement field
export function getFeaturedIds(gameData) {
  if (!gameData) return [];
  if (gameData.featuredElements) return gameData.featuredElements;
  return gameData.featuredElement ? [gameData.featuredElement] : [];
}

export function truncateDesc(desc, length = 100) {
  if (!desc) return '';
  const text = stripHtml(desc).replace(/&nbsp;/g, ' ').trim();
  return text.length > length ? `${text.substring(0, length).trim()}...` : text;
}

// one short line of type-specific info from the shortlist data
export function getFeaturedDetail(element) {
  if (element.type === 'level' && element.opt_pointMinimum) {
    return `${element.opt_pointMinimum.toLocaleString()} XP`;
  }
  if (element.type === 'quest' && element.checkpoints?.length) {
    return `${element.checkpoints.length} Checkpoint${element.checkpoints.length === 1 ? '' : 's'}`;
  }
  if (element.type === 'collection' && element.opt_itemList?.length) {
    return `${element.opt_itemList.length} Item${element.opt_itemList.length === 1 ? '' : 's'}`;
  }
  if (element.opt_itemType === 'contribution' && element.opt_contributionGoal) {
    return `Goal: ${element.opt_contributionGoal.toLocaleString()}`;
  }
  if (element.type === 'item' && element.opt_market && element.opt_marketPrice) {
    return `Price: ${element.opt_marketPrice.toLocaleString()}`;
  }
  return null;
}

// relative path from the player home page, or null if there is nowhere to go
export function getFeaturedLink(element, gameData) {
  const access = gameData?.access ?? [];
  switch (element.type) {
    case 'challenge':
      return access.includes('challenges') ? `challenges/${element.path}` : null;
    case 'quest':
      return access.includes('quests') ? `quests/${element.path}` : null;
    case 'badge':
      return `badges/${element.path}`;
    case 'level':
      return `levels/${element.path}`;
    case 'collection':
      return access.includes('items') ? 'inventory#collectible' : null;
    case 'item':
      if (element.opt_market && access.includes('store')) {
        return `marketplace#${element.opt_itemType}`;
      }
      if (element.opt_itemType !== 'prizepack' && access.includes('items')) {
        return `inventory#${element.opt_itemType}`;
      }
      return null;
    default:
      return null;
  }
}

// manager-chosen elements first (in order), then random fill up to FEATURED_MAX.
// randomOrder is an object (id -> number) the caller keeps for the life of the
// page so live playerData updates don't reshuffle the list
export function buildPlayerFeaturedList({
  gameData,
  gameElements,
  playerData,
  gameStats,
  teamMembers,
  randomOrder = {}
}) {
  if (!gameData || !gameElements || !playerData) return [];

  function isAvailable(element) {
    if (!isFeaturableElement(element)) return false;
    if (element.type === 'badge' && playerData.badges?.[element.id]) return false;
    if (element.type === 'level' && playerData.levels?.[element.id]) return false;
    return checkElementAccess({
      'playerData': playerData,
      'gameElements': gameElements,
      'elementId': element.id,
      'gameStats': gameStats,
      'teamMembers': teamMembers,
      'type': element.type,
      'bool': true
    });
  }

  // isChosen marks the manager's picks so they can be styled differently
  const chosen = getFeaturedIds(gameData)
    .map(id => gameElements[id])
    .filter(isAvailable)
    .slice(0, FEATURED_MAX)
    .map(e => ({ ...e, 'isChosen': true }));
  const chosenIds = chosen.map(e => e.id);
  const random = Object.values(gameElements)
    .filter(e => !chosenIds.includes(e.id) && isAvailable(e))
    .map(e => {
      if (randomOrder[e.id] === undefined) randomOrder[e.id] = Math.random();
      return e;
    })
    .sort((a, b) => randomOrder[a.id] - randomOrder[b.id])
    .slice(0, FEATURED_MAX - chosen.length);

  return [...chosen, ...random];
}
